import React, { useEffect, useState, useRef  } from 'react';
import api from '../../../shared/api/axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LikeListModal from './LikeListModal'; // Adjust the path if it's different
import LazyMedia from '../../components/LazyMedia';
import SidebarMyProfile from '../../sidebars/SidebarMyProfile';
import { useTranslation } from 'react-i18next';
import MyPostingPresentation from './MyPostingPresentation';
import Hls from "hls.js";

type MediaEntry =
  | { kind: 'image'; url: string }
  | {
      kind: 'video';
      videoId: string;
      url?: string | null;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
      musicId?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
    };

type MusicInfo = {
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
};


interface Post {
  _id: string;
  type: 'media';
  title: string;
  titleColor?: string;
  media: MediaEntry[];
  likes: number;
  likedBy?: string[];
  location?: {
    city: string;
    country: string;
  };
  createdAt: string;
  isHidden?: boolean;
  hiddenAt?: string | null;
}

// Api base URL normalization
const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';

const toImageUrl = (p: any): string => {
  const s = String(p || '').trim();
  if (!s) return '';
  if (/^(https?:|data:)/i.test(s)) return s;
  return `${IMAGE_CDN.replace(/\/$/, '')}/${s.replace(/^\/+/, '')}`;
};

const API_BASE = process.env.REACT_APP_API_BASE_URL!;
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const videoUrlOf = (m: Extract<MediaEntry, { kind: 'video' }>) => {
  if (m.hlsUrl) return m.hlsUrl;
  return hlsUrl(m.videoId);
};


const hlsUrl = (videoId: string) =>
  `${API_ORIGIN}/hls/playlist/${videoId}`;

const mediaLabelOf = (m: MediaEntry): string => {
  if (m.kind !== 'video') return '';
  const t = (m.musicTitle || '').trim();
  const a = (m.musicArtist || '').trim();
  if (!t && !a) return '';
  return t && a ? `${t} — ${a}` : (t || a);
};


const normalizeMediaToEntries = (p: any): MediaEntry[] => {
  const src = Array.isArray(p?.media)
    ? p.media
    : Array.isArray(p?.mediaUrls)
    ? p.mediaUrls
    : [];

  return src
    .map((m: any) => {
      if (typeof m === 'string') {
        return { kind: 'image', url: toImageUrl(m) };
      }

      if (!m || typeof m !== 'object') return null;

      if (m.isHls === true && typeof m.videoId === 'string' && m.videoId) {
        return {
          kind: 'video',
          videoId: m.videoId,
          url: m.url ?? m.hlsUrl ?? null,
          hlsUrl: m.hlsUrl ?? m.url ?? null,
          dashUrl: m.dashUrl ?? null,
          posterUrl: m.posterUrl ?? null,
          musicId: m.musicId ?? m.music?.id ?? null,
          musicTitle: m.musicTitle ?? m.music?.title ?? null,
          musicArtist: m.musicArtist ?? m.music?.artist ?? null,
        };
      }

      const videoId =
        m.videoId ??
        m.hlsId ??
        m.video?._id ??
        null;

      const looksVideo =
        m.kind === 'video' ||
        m.type === 'video' ||
        !!m.hlsUrl ||
        !!m.dashUrl ||
        (typeof m.url === 'string' && /\/hls\//i.test(m.url));

      if (looksVideo && videoId) {
        return {
          kind: 'video',
          videoId,
          url: m.url ?? m.hlsUrl ?? null,
          hlsUrl: m.hlsUrl ?? m.url ?? null,
          dashUrl: m.dashUrl ?? null,
          posterUrl: m.posterUrl ?? null,
          musicId: m.musicId ?? m.music?.id ?? null,
          musicTitle: m.musicTitle ?? m.music?.title ?? null,
          musicArtist: m.musicArtist ?? m.music?.artist ?? null,
        };
      }

      const img = m.url || m.image?.url || m.src || m.path || null;
      if (!img) return null;

      return { kind: 'image', url: toImageUrl(img) };
    })
    .filter(Boolean) as MediaEntry[];
};


// HlsPlayer

export const HLSPlayer = ({
  src,
  poster,
  muted,
  active,
  style = {},
  onOrientation,
  isLandscape
}: {
  src: string;
  poster?: string;
  muted: boolean;
  active: boolean;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  isLandscape?: boolean | null;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  //Play/pause
  const PlayIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <path d="M7 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
    <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
  </svg>
);

  //Loudspeakers
  const SpeakerOnIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15 9a4 4 0 0 1 0 6" />
    <path d="M18 7a7 7 0 0 1 0 10" />
  </svg>
);


const SpeakerOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

  /* ---------------- HLS attach ---------------- */
useEffect(() => {
  const video = videoRef.current;
  if (!video || !src) return;

  let hls: Hls | null = null;

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    video.load();
  } else if (Hls.isSupported()) {
    hls = new Hls({ startLevel: 0, enableWorker: true });
    hls.loadSource(src);
    hls.attachMedia(video);
  }

  return () => {
    try {
      if (hls) hls.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
    } catch {}
  };
}, [src]);

  /* ---------------- autoplay logic ---------------- */
 useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  if (!active) {
    v.pause();
    return;
  }

  if (!userPaused) {
    v.play().catch(() => {});
  }
}, [active, userPaused]);

  /* ---------------- progress tracking ---------------- */
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (v.duration * value) / 100;
  };

  /* ---------------- time ---------------- */
  const formatTime = (sec: number) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};


  /* ---------------- volume ---------------- */
  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  v.muted = isMuted;
}, [isMuted]);

  /* ---------------- play/pause ---------------- */
  const togglePlay = () => {
  const v = videoRef.current;
  if (!v) return;

  if (!v.paused && !v.ended) {
    v.pause();
    setUserPaused(true);
  } else {
    v.play().catch(() => {});
    setUserPaused(false);
  }
};




  
  /* ---------------- render ---------------- */

 return (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
    }}
    onMouseEnter={() => setShowControls(true)}
    onMouseLeave={() => setShowControls(false)}
  >
    {/* VIDEO */}
    <video
      ref={videoRef}
      poster={poster}
      muted={isMuted}
      playsInline
      preload="auto"
      controls={false}
      onPointerDown={togglePlay}
      onLoadedMetadata={(e) => {
        emitOrientation(e.currentTarget);
      }}
      onLoadedData={(e) => emitOrientation(e.currentTarget)}
      onCanPlay={(e) => emitOrientation(e.currentTarget)}
      onTimeUpdate={onTimeUpdate}
      onPlay={() => {
  setIsPlaying(true);
  setUserPaused(false);
}}
onPause={() => {
  setIsPlaying(false);
}}
//For the replay this onEnded if I dont want replay remove
onEnded={() => {
  const v = videoRef.current;
  if (!v) return;

  v.currentTime = 0;
  v.play().catch(() => {});
}}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        ...style,
      }}
    />

    {/* CONTROLS */}
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        opacity: showControls ? 1 : 0,
        transition: "opacity 200ms ease",
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: "hidden",
      }}
      onClick={(e) => e.stopPropagation()} 
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: isLandscape ? "7px 14px" : "5px 8px",
        }}
      >
        {/* LEFT — PLAY */}
        <div style={{ display: "flex", alignItems: "center" }}>
    <button
  onPointerDown={(e) => {
    e.stopPropagation();
    togglePlay();
  }}
  style={controlBtn}
>

  {isPlaying ? <PauseIcon /> : <PlayIcon />}

</button>

        </div>

        {/* CENTER — MAIN SCRUBBER */}
<div
  style={{
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    padding: "0 10px",
    maxWidth: isLandscape ? "90%" : "45%",   // 🔥 limits how wide the scrubber can ever become
    minWidth: 120,     // keeps it usable on small videos
  }}
>
  <input
    type="range"
    min={0}
    max={100}
    value={progress}
    onChange={(e) => handleScrub(Number(e.target.value))}
    className="custom-scrubber"
    style={{
      width: "100%",
      height: 3,
      background: `linear-gradient(
      to right,
      #d8cece 0%,
      #d8cece ${progress}%,
      #555 ${progress}%,
      #555 100%
    )`,
    }}
  />
</div>


        {/* RIGHT — TIME + VOLUME */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 1,     // ✅ allow shrinking
            minWidth: 0,
          }}
        >
{/* TIME */}
<span
  style={{
    fontSize: 11,
    color: "#bbb",
    whiteSpace: "nowrap",
    marginLeft: -2,
    marginRight: 7,
    fontVariantNumeric: "tabular-nums", // 👈 stable width numbers
  }}
>
  {formatTime(videoRef.current?.currentTime || 0)}/
  {formatTime(videoRef.current?.duration || 0)}
</span>


          {/* SPEAKER */}
          <button
  onClick={(e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  }}
  style={controlBtnVolume}
>
            {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
          </button>

          {/* VOLUME */}
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={isMuted ? 0 : volume}
            onChange={(e) => {
              const val = Number(e.target.value);
              setIsMuted(val === 0);
              handleVolume(val);
            }}
            className="volume-slider"
            style={{
              width: isLandscape ? 85 : 65,
              height: 2,
              background: `linear-gradient(
      to right,
      #bcb3b3 0%,
      #d8cece ${(isMuted ? 0 : volume) * 100}%,
      #666 ${(isMuted ? 0 : volume) * 100}%,
      #666 100%
    )`,
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

};

const controlBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,       // 👈 locks height
  lineHeight: 1,    // 👈 prevents font expansion
  padding: 0,       // 👈 removes vertical shift
};

const controlBtnVolume: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,
  lineHeight: 1,
  padding: 0,
};


// --- Orientation helpers (image+video) ---

function useMediaOrientation(url: string, isVideo: boolean) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let alive = true;

    if (!url) {
      setIsLandscape(null);
      return;
    }

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;

      const onMeta = () => {
        if (!alive) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        setIsLandscape(w && h ? w > h : null);
        cleanup();
      };
      const onErr = () => { if (alive) setIsLandscape(null); cleanup(); };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", onMeta);
        v.removeEventListener("error", onErr);
      };

      v.addEventListener("loadedmetadata", onMeta);
      v.addEventListener("error", onErr);
      try { v.load(); } catch {}
      return () => { alive = false; cleanup(); };
    }

    // image
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      setIsLandscape(img.naturalWidth > img.naturalHeight);
    };
    img.onerror = () => { if (alive) setIsLandscape(null); };
    img.src = url;

    return () => { alive = false; };
  }, [url, isVideo]);

  return isLandscape;
}

// --- Auto-fit renderer for one media item ---
// --- Auto-fit renderer for one media item (MyPostings) ---
const AutoFitMedia: React.FC<{
  url: string;
  isVideo: boolean;
  onClick?: () => void;
  label?: string;
  showMusic?: boolean; // hover toggle
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
  onMusicClick?: (info: MusicInfo) => void | Promise<void>;
  isActive?: boolean;
}> = ({
  url,
  isVideo,
  onClick,
  label,
  showMusic,
  musicId,
  musicTitle,
  musicArtist,
  onMusicClick,
  isActive = false,
}) => {
  const { t } = useTranslation();

  // Orientation
  const imageLandscape = useMediaOrientation(isVideo ? "" : url, false);
  const [videoLandscape, setVideoLandscape] = useState<boolean | null>(null);

  const isLandscape =
    isVideo ? (videoLandscape ?? null) : (imageLandscape ?? null);

  // 1) TILE: fixed height area in the horizontal scroller
  const tileStyle: React.CSSProperties = {
  position: "relative",
  flex: "0 0 auto",
  height: MEDIA_HEIGHT,
  maxHeight: MEDIA_HEIGHT,

  width: "fit-content",   // ✅ shrink-wrap so row can overflow
  maxWidth: "unset",      // ✅ don’t allow 100% tiles

  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
};

  // 2) STAGE: centers content, and allows music to be positioned below without shrinking media
  const stageStyle: React.CSSProperties = {
    position: "relative",
    height: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingBottom: 18, // reserve space so music text doesn't overlap media
    boxSizing: "border-box",
  };

  // 3) MEDIA BOX: shrink-wraps to media natural aspect (no width/height 100%)
  //    Portrait: constrain width a bit; Landscape: can use more width.
  const maxW = isLandscape === true ? LAND_TILE_WIDTH : PORTRAIT_TILE_MIN_WIDTH;

  const mediaBoxStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "auto",
    height: "auto",
    maxHeight: `calc(${MEDIA_HEIGHT} - 18px)`,
    maxWidth: maxW,
    borderRadius: 12,
  };

  const mediaStyle: React.CSSProperties = {
    width: "auto",
    height: "auto",
    maxHeight: `calc(${MEDIA_HEIGHT} - 18px)`,
    maxWidth: maxW,
    objectFit: "contain",
    borderRadius: 12,
    display: "block",
    cursor: onClick ? "pointer" : undefined,
  };

  // Music: OUTSIDE media (bottom, centered). Does not affect sizing.
  const musicStyle: React.CSSProperties = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    transform: "translateY(100%)", // push below media box
    padding: "6px 10px 10px",
    fontSize: 13,
    color: "#666",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    background:
      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 100%)",
    pointerEvents: "auto",
    opacity: showMusic ? 1 : 0,
    transition: "opacity 220ms ease",
  };

  return (
    <div style={tileStyle}>
      <div style={stageStyle}>
        <div style={mediaBoxStyle} onClick={onClick}>
          {/* MEDIA */}
          {isVideo ? (
            <HLSPlayer
              src={url}
              muted={true}
              active={isActive}
              onOrientation={(land) => setVideoLandscape(!!land)}
              isLandscape={videoLandscape ?? null}
              style={mediaStyle}
            />
          ) : (
            <LazyMedia
              src={url}
              type="img"
              alt="media"
              onClick={onClick}
              style={mediaStyle}
            />
          )}

          {/* MUSIC — outside media box bottom */}
          {isVideo && !!label && (
            <div
              title={label}
              style={musicStyle}
              onClick={(e) => e.stopPropagation()}
            >
              <span aria-hidden="true" style={{ flex: "0 0 auto" }}>
                🎵
              </span>

              {musicId ? (
                <Link
                  to={`/musicdetails/${musicId}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    minWidth: 0,
                    color: "#666",
                    fontWeight: 500,
                    fontSize: 12,
                    textDecoration: "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={
                    musicTitle
                      ? t("myPosts.media.openDetails", { title: musicTitle })
                      : t("myPosts.media.openUnknownDetails")
                  }
                >
                  {label}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMusicClick?.({
                      musicId: musicId ?? null,
                      musicTitle: musicTitle ?? null,
                      musicArtist: musicArtist ?? null,
                    });
                  }}
                  style={{
                    minWidth: 0,
                    color: "#666",
                    fontWeight: 500,
                    fontSize: 12,
                    background: "transparent",
                    border: 0,
                    padding: 0,
                    cursor: "pointer",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={
                    musicTitle
                      ? t("myPosts.media.openDetails", { title: musicTitle })
                      : t("myPosts.media.openUnknownDetails")
                  }
                >
                  {label}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const MyPostings = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isFetching, setIsFetching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [likesModalPostId, setLikesModalPostId] = useState<string | null>(null);
  const LIMIT = 120; // or keep 120 like visitor, but you can use 30/60 too
  const [hasMore, setHasMore] = useState(true);

const [offset, setOffset] = useState(0);
const [pageIndex, setPageIndex] = useState(0);
const feedRef = useRef<HTMLDivElement | null>(null);

const [pageHeight, setPageHeight] = useState(() => window.innerHeight);
  
  const location = useLocation();
  const focusPostId = (location.state as any)?.focusPostId;
  

  // Add state for presentation
  const [presentationPost, setPresentationPost] = useState<Post | null>(null);
  const [presentationIndex, setPresentationIndex] = useState<number | null>(null);
  // Music Info
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  const [deleteTargetPost, setDeleteTargetPost] = useState<Post | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);


const handleHoverEnter = (key: string) => {
  if (hoverTimeoutRef.current) {
    window.clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
  }
  setHoveredKey(key);
};
const handleHoverLeave = () => {
  hoverTimeoutRef.current = window.setTimeout(() => {
    setHoveredKey(null);
    hoverTimeoutRef.current = null;
  }, 500);
};
useEffect(() => () => {
  if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current);
}, []);

// Only one Video at a time
useEffect(() => {
  const onPlay = (e: Event) => {
    const el = e.target as HTMLVideoElement | null;
    if (!el || el.tagName !== 'VIDEO') return;

    // Pause every other <video> element
    document.querySelectorAll('video').forEach((v) => {
      if (v !== el) {
        try { v.pause(); } catch {}
      }
    });
  };

  // Capture phase so it catches plays from custom players too
  document.addEventListener('play', onPlay, true);
  return () => document.removeEventListener('play', onPlay, true);
}, []);


 const fetchMyPosts = async (useOffset: number, append = false) => {
  try {
    setIsFetching(true);

    const res = await api.get("/media/my-posts", {
      params: { offset: useOffset, limit: LIMIT },
    });

    const raw: any[] = Array.isArray(res.data) ? res.data : (res.data?.posts || []);
    const normalized: Post[] = raw.map((item: any) => ({
      isHidden: false,
      hiddenAt: null,
      ...item,
      _id: String(item._id),
      media: normalizeMediaToEntries(item),
    }));

    setPosts(prev => {
  const base = append ? prev : [];
  const merged = [...base, ...normalized];
  const byId = new Map(merged.map(p => [p._id, p]));
  return Array.from(byId.values()).slice(-MAX_POSTS_IN_MEMORY);
});

    setOffset(useOffset + LIMIT);
    setHasMore(raw.length === LIMIT); // same as Visitor
    setInitialLoading(false);
  } catch (err) {
    console.error("❌ Failed to fetch my posts:", err);
    setInitialLoading(false);
  } finally {
    setIsFetching(false);
  }
};
  
const start = Math.max(0, pageIndex - 1);
const end = Math.min(posts.length, pageIndex + 2);
const visiblePosts = posts.slice(start, end);
const MAX_POSTS_IN_MEMORY = 350;

  const MetaRow = ({ post }: { post: Post }) => (
    <div style={metaRowStyle}>
      <div style={metaLeftStyle}>
      <span>
  {t('myPosts.meta.created', {
    datetime: new Date(post.createdAt).toLocaleString(),
    type: post.type.toUpperCase(), // or map type to a separate label
  })}
</span>

{(post.location?.city || post.location?.country) && (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();

      const city = post.location?.city?.trim();
      const country = post.location?.country?.trim();

      if (!city || !country) return;

      navigate(
        `/location-collection?city=${encodeURIComponent(city)}&country=${encodeURIComponent(
          country
        )}`
      );
    }}
    style={{
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      color: "#777",
      fontSize: 13,
      cursor: "pointer",
    }}
    title={t('myPosts.meta.location', {
      city: post.location?.city || '',
      country: post.location?.country || '',
    })}
  >
    {t('myPosts.meta.location', {
      city: post.location?.city || '',
      country: post.location?.country || '',
    })}
  </button>
)}

<button
  type="button"
  onClick={() => setLikesModalPostId(post._id)}
  style={likesButtonStyle}
  title={t('myPosts.meta.showLikesTooltip')}
>
  {t('myPosts.meta.likes', { count: post.likes })}
</button>

<button
  onClick={() => openPostViewer(post, 0)}
  disabled={!post.media || post.media.length === 0}
  style={{ ...presentationBtnStyle, opacity: (!post.media || post.media.length === 0) ? 0.5 : 1 }}
  title={
    (!post.media || post.media.length === 0)
      ? t('myPosts.meta.noMediaToOpen')
      : t('myPosts.meta.openViewer')
  }
  >
  🎬 {t('myPosts.meta.presentation')}
</button>
    </div>
  </div>
);

 const handleOpenMusic: (info: MusicInfo) => void | Promise<void> =
  async (info) => {
    if (info.musicId) {
      navigate(`/musicdetails/${info.musicId}`);
      return;
    }

    const q = (info.musicTitle || info.musicArtist || '').trim();
    if (!q) return;

    try {
      const res = await api.get('/music', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : res.data?.tracks || [];
      if (list.length) {
        navigate(`/musicdetails/${list[0]._id}`);
      }
    } catch (e) {
      console.error('music lookup failed:', e);
    }
  };

const [hideBusy, setHideBusy] = useState<string | null>(null);

const handleHidePost = async (id: string) => {
  if (hideBusy) return;
  setHideBusy(id);
  try {
    const { data } = await api.post(`/post-visibility/${id}/hide`);
    setPosts(prev =>
      prev.map(p => p._id === id ? { ...p, isHidden: data.isHidden, hiddenAt: data.hiddenAt } : p)
    );
  } catch (e:any) {
    console.error('❌ Failed to hide post:', e?.response?.data || e.message);
    alert(e?.response?.data?.error || t('myPosts.errors.hideFailed'));
  } finally {
    setHideBusy(null);
  }
};

const handleUnhidePost = async (id: string) => {
  if (hideBusy) return;
  setHideBusy(id);
  try {
    const { data } = await api.post(`/post-visibility/${id}/unhide`);
    setPosts(prev =>
      prev.map(p => p._id === id ? { ...p, isHidden: data.isHidden, hiddenAt: data.hiddenAt } : p)
    );
  } catch (e:any) {
    console.error('❌ Failed to unhide post:', e?.response?.data || e.message);
    alert(e?.response?.data?.error || t('myPosts.errors.unhideFailed'));
  } finally {
    setHideBusy(null);
  }
};


const openPostViewer = (post: Post, index: number) => {
  setPresentationPost(post);
  setPresentationIndex(index);
};


useEffect(() => {
  const el = feedRef.current;
  if (!el) return;

  const update = () => setPageHeight(el.clientHeight || window.innerHeight);
  update();

  window.addEventListener("resize", update);
  return () => window.removeEventListener("resize", update);
}, []);


 // Hide scrollbars globally for elements with .hScroll
 useEffect(() => {
  const style = document.createElement('style');
  style.setAttribute('data-hide-scrollbars', 'true');
  style.innerHTML = `
    /* Firefox + legacy IE/Edge */
    .hScroll { scrollbar-width: none; -ms-overflow-style: none; scrollbar-color: transparent transparent; }
    /* Chromium/WebKit */
    .hScroll::-webkit-scrollbar { display: none; width: 0 !important; height: 0 !important; }
    .hScroll::-webkit-scrollbar-thumb { background: transparent; }
  `;
  document.head.appendChild(style);
  return () => { document.head.removeChild(style); };
}, []);

  //Prefetching
useEffect(() => {
  setPosts([]);
  setOffset(0);
  setHasMore(true);
  setInitialLoading(true);
  setPageIndex(0);

  fetchMyPosts(0, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);
  
useEffect(() => {
  setPageIndex(prev => Math.min(prev, Math.max(0, posts.length - 1)));
}, [posts.length]);

useEffect(() => {
  if (!focusPostId) return;

  const el = document.querySelector<HTMLElement>(
    `[data-post-id="${focusPostId}"]`
  );
  if (!el) return;

  // ✅ instant jump to the post (no animation)
  el.scrollIntoView({
    behavior: 'auto',      // <- this is the important bit (was 'smooth')
    block: 'center',       // or 'start' if you want it at the very top
  });

  // Optional: highlight the card briefly so it's obvious
  el.style.outline = '2px solid #d1ac52';

  const timeout = window.setTimeout(() => {
    el.style.outline = 'none';
  }, 6000);

  return () => window.clearTimeout(timeout);
}, [focusPostId, posts]);

useEffect(() => {
  const styleId = 'spin-keyframes';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;

  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);

  return () => {
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();
  };
}, []);

  /**
   * Delete a post
   */
  const handleDelete = (post: Post) => {
  setDeleteTargetPost(post);
};

const confirmDelete = async () => {
  if (!deleteTargetPost) return;

  try {
    setDeleteBusy(true);
    await api.delete(`/media/${deleteTargetPost._id}`);

    setPosts(prev =>
      prev.filter(p => p._id !== deleteTargetPost._id)
    );

    setDeleteTargetPost(null);
  } catch (err: any) {
    console.error('❌ Failed to delete post:', err.response?.data || err.message);
    alert(t('myPosts.actions.deleteFailed'));
  } finally {
    setDeleteBusy(false);
  }
};


  const renderPostContent = (post: Post) => {
    const fullMedia = (post.media || []).map((entry, idx) => {
      const abs = entry.kind === 'video'
  ? videoUrlOf(entry)
  : entry.url;

      const video = entry.kind === 'video';
      const label = mediaLabelOf(entry);
      const hk = `${post._id}:${idx}`;
      const videoEntry = entry.kind === 'video' ? entry : null;
      const isPostActive = posts[pageIndex]?._id === post._id;
      
      return (
        <div
  key={abs || idx}
  style={mediaItemWrapStyle}
  role={video ? 'group' : 'button'}
  tabIndex={video ? -1 : 0}

  // 🚀 IMAGE click opens presentation
  onClick={!video ? () => openPostViewer(post, idx) : undefined}

  // 🚀 keyboard only for images
  onKeyDown={(e) => {
    if (!video && e.key === 'Enter') {
      openPostViewer(post, idx);
    }
  }}

  onMouseEnter={() => handleHoverEnter(hk)}
  onMouseLeave={handleHoverLeave}
>

 <AutoFitMedia
  url={abs}
  isVideo={entry.kind === 'video'}
  onClick={
    entry.kind === "image"
      ? () => openPostViewer(post, idx)
      : undefined
  }
  label={label}
  showMusic={hoveredKey === hk}
  musicId={videoEntry?.musicId ?? null}
  musicTitle={videoEntry?.musicTitle ?? null}
  musicArtist={videoEntry?.musicArtist ?? null}
  onMusicClick={handleOpenMusic}
  isActive={isPostActive}
/>

  
        </div>
      );
    });
  
    return (
      <>
        <div style={mediaContainerStyle} className="hScroll">{fullMedia}</div>
        <MetaRow post={post} />
      </>
    );
  };
  
  
    return (
      <div
        style={{
          display: 'flex',
          background: '#000',
          flexGrow: 1,
          minHeight: '100vh',
          width: '100%',
        }}
      >
        <SidebarMyProfile/>
    
        {/* Main Content */}
 <div
  style={{
    flex: 1,
    minWidth: 0,
    padding: "0 48px 0 75px", // ✅ no top/bottom padding
    marginLeft: "14%",
    marginTop: 0,            // ✅ no top margin
    color: "#bbb",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid #1f1f1f",
    height: "100dvh",
    overflow: "hidden",
  }}
>

{initialLoading ? (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
      color: '#9ca3af',
      gap: 14,
    }}
  >
    {/* spinner */}
    <div
      style={{
        width: 28,
        height: 28,
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #cbd5f5',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />

    {/* text */}
    <div style={{ fontSize: 18, letterSpacing: 0.3 }}>
      {t('myPosts.loading') || 'Loading my posts…'}
    </div>
  </div>

) : posts.length === 0 ? (
  <p
    style={{
      fontSize: '4rem',
      lineHeight: 1,
      marginLeft: '34%',
      marginTop: '27%',
    }}
  >
    📭
  </p>
) : (

<div
  ref={feedRef}
  onScroll={(e) => {
    const el = e.currentTarget;

    const h = el.clientHeight || window.innerHeight;
    const raw = Math.floor((el.scrollTop + h / 2) / h);

    if (raw !== pageIndex) setPageIndex(raw);

    // Infinite load trigger (same as Visitor)
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200;
    if (nearBottom && !isFetching && hasMore) {
      fetchMyPosts(offset, true);
    }
  }}
  style={{
    overflowY: "auto",
    overflowX: "hidden",
    height: "100%", 
    scrollSnapType: "y mandatory",
    overscrollBehaviorY: "contain", 
  }}
>
  {/* top spacer */}
  {start > 0 && <div style={{ height: start * pageHeight }} />}

  {/* visible posts only */}
{visiblePosts.map((post) => (
  <div
    key={post._id}
    data-post-id={post._id}
    style={{ ...cardStyleBase, height: pageHeight,  scrollSnapAlign: "start", scrollSnapStop: "always", }}
  >
    {renderPostContent(post)}

    {/* Action bar */}
    <div style={{ display: "flex", marginTop: 4 }}>
      <button
        onClick={() => {
          navigate(`/posting/edit/${post._id}`, {
            state: {
              sidebarData: {
                hashtags: [],
                title: post.title || "",
                titleColor: post.titleColor || "",
              },
            },
          });
        }}
        style={buttonStyle}
      >
        ✏️ {t("myPosts.actions.edit")}
      </button>

      <button onClick={() => handleDelete(post)} style={deleteButtonStyle}>
        🗑️ {t("myPosts.actions.delete")}
      </button>

      <button
        onClick={() =>
          post.isHidden ? handleUnhidePost(post._id) : handleHidePost(post._id)
        }
        disabled={hideBusy === post._id}
        style={{
          ...buttonStyle,
          marginLeft: 8,
          backgroundColor: post.isHidden ? "#1e824c" : "#555",
          opacity: hideBusy === post._id ? 0.6 : 1,
          cursor: hideBusy === post._id ? "not-allowed" : "pointer",
        }}
        title={
          post.isHidden
            ? t("myPosts.actions.unhideTooltip")
            : t("myPosts.actions.hideTooltip")
        }
      >
        {hideBusy === post._id
          ? t("myPosts.actions.saving")
          : post.isHidden
          ? `👀 ${t("myPosts.actions.unhide")}`
          : `🙈 ${t("myPosts.actions.hide")}`}
      </button>
    </div>
  </div>
))}

  {/* bottom spacer */}
  {end < posts.length && (
    <div style={{ height: (posts.length - end) * pageHeight }} />
  )}
</div>
          )}
    
          {likesModalPostId && (
  <LikeListModal
    postId={likesModalPostId}
    onClose={() => setLikesModalPostId(null)}
  />
)}
        </div>

{presentationPost && presentationIndex !== null && (
  <MyPostingPresentation
    post={presentationPost}
    startIndex={presentationIndex}
    onClose={() => {
      setPresentationPost(null);
      setPresentationIndex(null);
    }}
  />
)}

{deleteTargetPost && (
  <div style={confirmOverlay}>
    <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
      <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 600 }}>
        {t('myPosts.actions.confirmDelete')}
      </div>

      <button
        onClick={confirmDelete}
        disabled={deleteBusy}
        style={dangerBtn}
      >
        {deleteBusy ? t('common.deleting') : t('common.confirm')}
      </button>

      <button
        onClick={() => setDeleteTargetPost(null)}
        disabled={deleteBusy}
        style={cancelBtn}
      >
        {t('common.cancel')}
      </button>
    </div>
  </div>
)}

      </div>
    ); 
};

// Styles
const MEDIA_HEIGHT = '85vh';
const LAND_TILE_WIDTH = MEDIA_HEIGHT; // e.g. '80vh'
const PORTRAIT_TILE_MIN_WIDTH = 'clamp(220px, 36vw, 520px)';

const cardStyleBase: React.CSSProperties = {
  background: "#000",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  height: "100%",          // ✅ NOT 100dvh
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderBottom: "1px solid #1f1f1f",
  paddingTop: 15, 
};

const buttonStyle: React.CSSProperties = {
  padding: '4px 10px',
  width: '150px',
  marginRight: '25px',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#380675',
  color: '#ccc',
  cursor: 'pointer',
  fontSize: '13px',
};

const deleteButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  border: '1px solid #bd0808',
};

const mediaContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: "flex-start",
  width: "100%",        // ✅
  minWidth: 0,   
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: '30px',
  padding: "10px 0 22px 0",   // ✅ more space below media
  scrollSnapType: 'x mandatory',
  scrollBehavior: 'smooth',
  WebkitOverflowScrolling: 'touch',
  height: MEDIA_HEIGHT,
  overscrollBehaviorX: 'contain',
};


const metaRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  color: "#777",
  fontSize: 13,

  marginTop: 10,       // ✅ lower / more separation from media
  marginBottom: 6,     // ✅ keeps it close to the action buttons

  paddingRight: "2%",
  gap: 10,
  flexWrap: "wrap",
};

const metaLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

const presentationBtnStyle: React.CSSProperties = {
  backgroundColor: '#000',
  color: '#bbb',
  border: '0.8px solid #333',
  borderRadius: 6,
  padding: '3px 8px',
  fontSize: 12,
  cursor: 'pointer',
  lineHeight: 1.2,
};

const mediaItemWrapStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  flex: "0 0 auto",
  scrollSnapAlign: "center",
  height: "100%",
  maxHeight: MEDIA_HEIGHT,

  width: "fit-content",     // ✅ critical
  maxWidth: "unset",        // ✅ critical
};

const likesButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#f97373',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  padding: '2px 6px',
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "92%",
  maxWidth: 420,
  background: "#000",
  borderRadius: 16,
  padding: 22,
  textAlign: "center",
  border: "1px solid #333",
  color: "#e5e7eb",
  boxShadow: "0 25px 80px rgba(0,0,0,0.9)",
};

const dangerBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "#b91c1c",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 12,
  border: "1px solid #333",
  background: "transparent",
  color: "#cbd5f5",
  cursor: "pointer",
};

export default MyPostings; 
